import React, {useEffect, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Stack,
    Typography
} from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {toast} from 'react-toastify';
import {formatProcessingRate, formatSecondsRemaining, useMappingJob} from '../useMappingJob';
import {DescriptorApplyProps, ReportSource, StartMappingJobRequest} from '../descriptor-apply-types';
import newOccurrenceService from '../../../../../../services/new-occurrence.service';
import occurrenceReportDescriptorService from '../../../../../../services/occurrence-report-descriptor.service';
import {useWorkspaceContext} from '../../../../../../context/workspaceContext';
import {ResourceType} from '../../../../../../@types/resource-type';

/**
 * Component for applying published descriptors to occurrence reports.
 * Single unified panel that adapts to all mapping states:
 *   - Not mapped  (0% coverage)
 *   - Partially mapped
 *   - Fully mapped
 *   - Mapping in progress (live progress bar, auto-polled)
 */
export const DescriptorApply: React.FC<DescriptorApplyProps> = ({
    descriptorId,
    descriptorName = 'Descriptor',
    onJobStarted,
    onJobCompleted,
    onJobFailed
}) => {
    const {
        currentJob,
        existingJob,
        isLoading,
        error,
        startJob,
        cancelJob,
        checkExistingJob,
        deleteMapping,
    } = useMappingJob(descriptorId);

    const { subscribeToResourceUpdates } = useWorkspaceContext();

    const [selectedSource, setSelectedSource] = useState<string>('');
    const [reportSources, setReportSources] = useState<ReportSource[]>([]);
    const [loadingSources, setLoadingSources] = useState(true);
    const [sourcesError, setSourcesError] = useState<string | null>(null);
    const [unmappedCount, setUnmappedCount] = useState<number | null>(null);
    const [loadingCoverage, setLoadingCoverage] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [showResultsDialog, setShowResultsDialog] = useState(false);
    const [jobResults, setJobResults] = useState<any>(null);
    const [loadingResults, setLoadingResults] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    // Derived state
    const isJobActive = currentJob !== null && ['PENDING', 'RUNNING'].includes(currentJob.status);
    const totalInSource: number = reportSources.find(s => s.value === selectedSource)?.count ?? 0;
    const mappedCount: number | null = unmappedCount !== null ? totalInSource - unmappedCount : null;
    const percentMapped: number =
        mappedCount !== null && totalInSource > 0
            ? Math.min(100, (mappedCount / totalInSource) * 100)
            : 0;

    // ── Effects ──────────────────────────────────────────────────────────────

    // Fetch report sources on mount
    useEffect(() => {
        const fetchReportSources = async (): Promise<void> => {
            try {
                setLoadingSources(true);
                const sources = await newOccurrenceService.getReportSources();
                const transformed: ReportSource[] = sources.map(source => ({
                    value: source.sourceName,
                    label: `${source.sourceName} Reports`,
                    count: source.reportCount,
                    description: source.description || source.sourceName
                }));
                setReportSources(transformed);
                setSourcesError(null);
            } catch (err: any) {
                const msg = err.message || 'Failed to load report sources';
                setSourcesError(msg);
                toast.error(msg);
            } finally {
                setLoadingSources(false);
            }
        };
        fetchReportSources();
    }, []);


    // Check for existing job when source changes
    useEffect(() => {
        if (selectedSource && !currentJob) {
            checkExistingJob([selectedSource]);
        }
    // eslint-disable-next-line
    }, [selectedSource]);

    // When an active job is restored from localStorage (e.g. user reopened the tab),
    // auto-select its source so the panel renders and shows live progress.
    useEffect(() => {
        if (currentJob && !selectedSource) {
            setSelectedSource(currentJob.source);
        }
    // eslint-disable-next-line
    }, [currentJob?.jobId]); // Fire only when a new job ID appears, not on every status update

    // Fetch live coverage from DB whenever the selected source changes
    useEffect(() => {
        if (!selectedSource) {
            setUnmappedCount(null);
            return;
        }
        let cancelled = false;
        setLoadingCoverage(true);
        occurrenceReportDescriptorService.getMappingCoverage(descriptorId, [selectedSource])
            .then(result => { if (!cancelled) setUnmappedCount(result.unmappedCount); })
            .catch(() => { if (!cancelled) setUnmappedCount(null); })
            .finally(() => { if (!cancelled) setLoadingCoverage(false); });
        return (): void => { cancelled = true; };
    }, [selectedSource, descriptorId]);

    // Re-fetch coverage after a job reaches a terminal state
    useEffect(() => {
        if (['COMPLETED', 'CANCELLED'].includes(currentJob?.status ?? '') && selectedSource) {
            setLoadingCoverage(true);
            occurrenceReportDescriptorService.getMappingCoverage(descriptorId, [selectedSource])
                .then(result => { setUnmappedCount(result.unmappedCount); })
                .catch(() => { setUnmappedCount(null); })
                .finally(() => { setLoadingCoverage(false); });
        }
    // eslint-disable-next-line
    }, [currentJob?.status]);

    // Re-fetch coverage after a mapping import completes for this descriptor
    useEffect(() => {
        const unsubscribe = subscribeToResourceUpdates(
            ResourceType.OCCURRENCE_REPORT_DESCRIPTORS,
            (event) => {
                if (event.source === 'refresh' && event.id === descriptorId && selectedSource) {
                    setLoadingCoverage(true);
                    occurrenceReportDescriptorService.getMappingCoverage(descriptorId, [selectedSource])
                        .then(result => { setUnmappedCount(result.unmappedCount); })
                        .catch(() => { setUnmappedCount(null); })
                        .finally(() => { setLoadingCoverage(false); });
                }
            }
        );
        return unsubscribe;
    }, [subscribeToResourceUpdates, descriptorId, selectedSource]);

    // Notify parent when job status changes
    useEffect(() => {
        if (currentJob?.status === 'COMPLETED' && onJobCompleted) {
            onJobCompleted(currentJob);
        } else if (currentJob?.status === 'FAILED' && onJobFailed) {
            onJobFailed(currentJob.errorMessage || 'Job failed');
        } else if (currentJob?.status === 'PENDING' && onJobStarted) {
            onJobStarted(currentJob.jobId);
        }
    }, [currentJob?.status]);

    // ── Handlers ─────────────────────────────────────────────────────────────

    const handleMapDescriptor = async (): Promise<void> => {
        if (!selectedSource) {
            toast.error('Please select a report source');
            return;
        }
        const request: StartMappingJobRequest = {
            descriptorId,
            source: selectedSource,
            options: {
                confidenceThreshold: 0.7,
                includeUnmapped: false,
                batchSize: 100
            }
        };
        await startJob(request);
    };

    const handleDeleteMapping = async (): Promise<void> => {
        if (!selectedSource) return;
        setShowDeleteConfirm(false);
        await deleteMapping([selectedSource]);
        // Refresh coverage after deletion (deleteMapping cleared job state)
        setLoadingCoverage(true);
        occurrenceReportDescriptorService.getMappingCoverage(descriptorId, [selectedSource])
            .then(result => { setUnmappedCount(result.unmappedCount); })
            .catch(() => { setUnmappedCount(null); })
            .finally(() => { setLoadingCoverage(false); });
    };

    const handleExportMapping = async (): Promise<void> => {
        if (!selectedSource) return;
        setIsExporting(true);
        try {
            const exportData = await occurrenceReportDescriptorService.exportMappingData(descriptorId, selectedSource);
            const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            const safeName = (descriptorName || 'descriptor').replace(/[^a-z0-9_-]/gi, '_');
            const safeSource = selectedSource.replace(/[^a-z0-9_-]/gi, '_');
            anchor.href = url;
            anchor.download = `${safeName}_${safeSource}_mapping.json`;
            anchor.click();
            URL.revokeObjectURL(url);
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Failed to export mapping data';
            toast.error(msg);
        } finally {
            setIsExporting(false);
        }
    };

    const viewJobResults = async (executionId: number): Promise<void> => {
        setLoadingResults(true);
        setShowResultsDialog(true);
        try {
            const response = await occurrenceReportDescriptorService.getJobResults(executionId);
            if (response?.summary) {
                setJobResults({
                    totalReports: response.details.statistics.totalReportsProcessed || 0,
                    successfullyProcessed: response.details.statistics.successfulReports || 0,
                    failedMappings: response.details.statistics.failedReports || 0,
                    averageConfidence: response.details.statistics.averageConfidenceScore || 0,
                    processingTimeSeconds: response.executionDurationMillis ? response.executionDurationMillis / 1000 : 0,
                    summary: response.summary,
                    details: response.details,
                    resultData: response
                });
            } else {
                setJobResults(response || null);
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to load job results:', err);
            toast.error('Failed to load job results');
            setJobResults(null);
        } finally {
            setLoadingResults(false);
        }
    };

    // ── Status chip ──────────────────────────────────────────────────────────

    const getStatusChip = (): React.ReactElement => {
        if (isJobActive) {
            return (
                <Chip
                    label="Mapping in Progress"
                    color="primary"
                    size="small"
                    icon={<CircularProgress size={12} color="inherit" />}
                />
            );
        }
        if (loadingCoverage || mappedCount === null) {
            return <Chip label="Loading…" size="small" />;
        }
        if (unmappedCount !== null && unmappedCount <= 0) {
            return <Chip label="Fully Mapped" color="success" size="small" icon={<CheckCircleOutlineIcon />} />;
        }
        if (mappedCount === 0) {
            return <Chip label="Not Mapped" size="small" />;
        }
        return <Chip label="Partially Mapped" color="warning" size="small" icon={<ErrorOutlineIcon />} />;
    };

    // ── Date formatting ───────────────────────────────────────────────────────

    const formatDateTime = (dateString: string | null | undefined): string => {
        if (!dateString) return 'Unknown';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Unknown';
            return date.toLocaleString('en-US', {
                year: 'numeric', month: 'long', day: 'numeric',
                hour: '2-digit', minute: '2-digit'
            });
        } catch {
            return 'Unknown';
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Apply Descriptor to Reports
            </Typography>

            {error && (
                <Alert severity="error" sx={{mb: 2}}>
                    {error}
                </Alert>
            )}

            {/* Source selector — disabled while a job is running */}
            <Box sx={{mb: 2}}>
                <FormControl fullWidth disabled={isLoading || loadingSources || isJobActive}>
                    <InputLabel>Report Source</InputLabel>
                    <Select
                        value={selectedSource}
                        label="Report Source"
                        onChange={(e): void => setSelectedSource(e.target.value)}
                    >
                        {loadingSources ? (
                            <MenuItem disabled>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <CircularProgress size={16} />
                                    <Typography variant="body2">Loading report sources...</Typography>
                                </Box>
                            </MenuItem>
                        ) : sourcesError ? (
                            <MenuItem disabled>
                                <Typography variant="body2" color="error">{sourcesError}</Typography>
                            </MenuItem>
                        ) : reportSources.length === 0 ? (
                            <MenuItem disabled>
                                <Typography variant="body2">No report sources available</Typography>
                            </MenuItem>
                        ) : (
                            reportSources.map((source) => (
                                <MenuItem key={source.value} value={source.value}>
                                    <Box>
                                        <Typography variant="body1">
                                            {source.label} ({source.count.toLocaleString()} reports)
                                        </Typography>
                                        {source.description && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                {source.description}
                                            </Typography>
                                        )}
                                    </Box>
                                </MenuItem>
                            ))
                        )}
                    </Select>
                </FormControl>
            </Box>

            {/* Unified mapping panel — visible whenever a source is selected */}
            {selectedSource && (
                <Paper elevation={1} sx={{p: 2}}>
                    <Stack spacing={2}>

                        {/* Header: source name + status chip + cancel button when active */}
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="subtitle1" fontWeight="medium">
                                    {selectedSource} Mapping Status
                                </Typography>
                                {getStatusChip()}
                            </Box>
                            {isJobActive && (
                                <Button
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                    startIcon={<CancelIcon />}
                                    onClick={(): void => setShowCancelConfirm(true)}
                                    disabled={isLoading}
                                >
                                    Cancel
                                </Button>
                            )}
                        </Box>

                        {/* Coverage / progress bar */}
                        <Box>
                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                                <Typography variant="body2" color="text.secondary">
                                    {isJobActive ? 'Mapping Progress' : 'Mapping Coverage'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {isJobActive
                                        ? `${Math.round(currentJob!.progress || 0)}%`
                                        : loadingCoverage ? '…' : `${percentMapped.toFixed(1)}%`
                                    }
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant={isJobActive || loadingCoverage ? 'indeterminate' : 'determinate'}
                                value={isJobActive ? (currentJob!.progress || 0) : percentMapped}
                                sx={{height: 8, borderRadius: 1}}
                            />
                            <Box display="flex" justifyContent="space-between" mt={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    {isJobActive
                                        ? `${(currentJob!.itemsProcessed || 0).toLocaleString()} of ${(currentJob!.totalItems || totalInSource).toLocaleString()} reports processed`
                                        : loadingCoverage
                                            ? 'Loading coverage data…'
                                            : mappedCount !== null
                                                ? `${mappedCount.toLocaleString()} of ${totalInSource.toLocaleString()} reports mapped`
                                                : `${totalInSource.toLocaleString()} total reports`
                                    }
                                </Typography>
                                {!isJobActive && !loadingCoverage && unmappedCount !== null && unmappedCount > 0 && (
                                    <Typography variant="caption" color="warning.main">
                                        {unmappedCount.toLocaleString()} remaining
                                    </Typography>
                                )}
                            </Box>
                            {/* Rate + ETA when running */}
                            {isJobActive && (currentJob!.processingRatePerSecond || currentJob!.estimatedSecondsRemaining) && (
                                <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                                    {currentJob!.processingRatePerSecond
                                        ? formatProcessingRate(currentJob!.processingRatePerSecond)
                                        : ''
                                    }
                                    {currentJob!.estimatedSecondsRemaining && currentJob!.estimatedSecondsRemaining > 0
                                        ? ` · ${formatSecondsRemaining(currentJob!.estimatedSecondsRemaining)} remaining`
                                        : ''
                                    }
                                </Typography>
                            )}
                        </Box>

                        {/* Job metadata — shown when an existing job is known and not actively running */}
                        {existingJob && !isJobActive && (
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Last run:</strong>{' '}
                                        {formatDateTime(existingJob.status?.completedAt || existingJob.status?.submittedAt)}
                                    </Typography>
                                    {existingJob.submittedBy && (
                                        <Typography variant="body2" color="text.secondary">
                                            <strong>Run by:</strong> {existingJob.submittedBy}
                                        </Typography>
                                    )}
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    Job #{existingJob.executionId}
                                </Typography>
                            </Box>
                        )}


                        {/* Failed job error message */}
                        {currentJob?.status === 'FAILED' && currentJob.errorMessage && (
                            <Alert severity="error">{currentJob.errorMessage}</Alert>
                        )}

                        {/* Actions */}
                        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                            {/* Map button — shown when not running and there are unmapped reports */}
                            {!isJobActive && (unmappedCount === null || unmappedCount > 0) && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={isLoading ? <CircularProgress size={20} /> : <MapIcon />}
                                    onClick={handleMapDescriptor}
                                    disabled={isLoading || loadingCoverage}
                                >
                                    {!loadingCoverage && unmappedCount !== null && unmappedCount > 0
                                        ? `Map ${unmappedCount.toLocaleString()} New Reports`
                                        : 'Map Reports'
                                    }
                                </Button>
                            )}

                            {/* View last results — shown when a prior job exists */}
                            {!isJobActive && existingJob && (
                                <Button
                                    variant="outlined"
                                    startIcon={<VisibilityIcon />}
                                    onClick={(): void => { viewJobResults(existingJob.executionId); }}
                                >
                                    View Last Results
                                </Button>
                            )}

                            {/* Export mapping — shown when something has been mapped */}
                            {!isJobActive && mappedCount !== null && mappedCount > 0 && (
                                <Button
                                    variant="outlined"
                                    startIcon={isExporting ? <CircularProgress size={16} /> : <FileDownloadIcon />}
                                    onClick={handleExportMapping}
                                    disabled={isExporting || isLoading || loadingCoverage}
                                >
                                    Export Mapping
                                </Button>
                            )}

                            {/* Delete mapping — shown when something has been mapped */}
                            {!isJobActive && mappedCount !== null && mappedCount > 0 && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={isLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
                                    onClick={(): void => setShowDeleteConfirm(true)}
                                    disabled={isLoading}
                                >
                                    Delete Mapping
                                </Button>
                            )}
                        </Stack>

                    </Stack>
                </Paper>
            )}

            {/* ── Dialogs ── */}

            {/* Results dialog */}
            <Dialog
                open={showResultsDialog}
                onClose={(): void => setShowResultsDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Mapping Job Results
                    {existingJob && (
                        <Typography variant="body2" color="text.secondary">
                            Job #{existingJob.executionId} — {
                                existingJob.request?.parameters?.sourceNames?.[0] ||
                                selectedSource ||
                                'Unknown Source'
                            }
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    {loadingResults ? (
                        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                            <CircularProgress />
                        </Box>
                    ) : jobResults ? (
                        <Stack spacing={3}>
                            <Paper variant="outlined" sx={{p: 2}}>
                                <Typography variant="h6" gutterBottom>Job Results</Typography>
                                <Stack spacing={1}>
                                    {jobResults.details && (
                                        <>
                                            <Typography variant="body2">
                                                <strong>Total Reports Processed:</strong> {jobResults.details.statistics.totalReportsProcessed || 0}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Successfully Processed:</strong> {jobResults.details.statistics.successfulReports || 0}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Failed:</strong> {jobResults.details.statistics.failedReports || 0}
                                            </Typography>
                                            {jobResults.details.statistics.totalMappings !== undefined && (
                                                <Typography variant="body2">
                                                    <strong>Total Mappings Created:</strong> {jobResults.details.statistics.totalMappings}
                                                </Typography>
                                            )}
                                        </>
                                    )}
                                    {jobResults.executionDurationMillis && (
                                        <Typography variant="body2">
                                            <strong>Processing Time:</strong> {(jobResults.executionDurationMillis / 1000).toFixed(1)} seconds
                                        </Typography>
                                    )}
                                </Stack>
                            </Paper>
                            <Paper variant="outlined" sx={{p: 2}}>
                                <Typography variant="h6" gutterBottom>Full Result Details</Typography>
                                <pre style={{overflow: 'auto', maxHeight: '400px', fontSize: '12px', backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '4px'}}>
                                    {JSON.stringify(jobResults, null, 2)}
                                </pre>
                            </Paper>
                        </Stack>
                    ) : (
                        <Alert severity="info">No results available for this job.</Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={(): void => setShowResultsDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Delete confirmation dialog */}
            <Dialog
                open={showDeleteConfirm}
                onClose={(): void => setShowDeleteConfirm(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Delete Mapping Confirmation</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{mb: 2}}>
                        This action will permanently delete all descriptor mappings for {selectedSource} reports.
                    </Alert>
                    <Typography variant="body1">
                        Are you sure you want to delete the mapping between &quot;{descriptorName}&quot; and {selectedSource} reports?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mt: 2}}>
                        This will remove all descriptor term associations from the reports. The reports themselves will not be deleted.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={(): void => setShowDeleteConfirm(false)} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteMapping}
                        color="error"
                        variant="contained"
                        disabled={isLoading}
                        startIcon={isLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
                    >
                        {isLoading ? 'Deleting...' : 'Delete Mapping'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cancel job confirmation dialog */}
            <Dialog
                open={showCancelConfirm}
                onClose={(): void => setShowCancelConfirm(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Cancel Descriptor Mapping Job?</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Are you sure you want to cancel this job?
                    </Typography>
                    {currentJob && (
                        <Box sx={{my: 2}}>
                            <Typography variant="body2" color="text.secondary">
                                <strong>{currentJob.itemsProcessed?.toLocaleString() || 0}</strong> of{' '}
                                <strong>{currentJob.totalItems?.toLocaleString() || '?'}</strong> reports have been processed.
                            </Typography>
                        </Box>
                    )}
                    <Alert severity="info" sx={{mt: 2}}>
                        Results for already-processed reports will be kept.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={(): void => setShowCancelConfirm(false)} variant="outlined">
                        Keep Running
                    </Button>
                    <Button
                        onClick={async (): Promise<void> => {
                            setShowCancelConfirm(false);
                            if (currentJob) await cancelJob(currentJob.jobId);
                        }}
                        color="error"
                        variant="contained"
                        startIcon={<CancelIcon />}
                    >
                        Cancel Job
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
